import { createOpinion } from '@/api/query/opinion';
import { useLanguageData } from '@/common/hooks/useLanguageData';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
	OPINION_DECISION,
	OPINION_SOURCE,
} from '../constants/opinion.constants';

const getUserDeviceDescription = () => {
	const deviceDescription = {
		platform: navigator.platform,
		userAgent: navigator.userAgent,
		language: navigator.language,
		screen: {
			width: window.screen.width,
			height: window.screen.height,
			pixelRatio: window.devicePixelRatio,
		},
	};
	return JSON.stringify(deviceDescription);
};

const useChatFeedback = (
	person,
	{ source = OPINION_SOURCE.PERSON, ratedMessage } = {},
) => {
	const t = useLanguageData('chat');
	const [activeDecision, setActiveDecision] = useState(null);
	const [text, setText] = useState('');
	const [selectedDecision, setSelectedDecision] = useState(null);

	const handleCreateHandMutation = useMutation({
		mutationFn: (payload) => createOpinion(payload),
	});

	const handleIconClick = (clickedDecision) => {
		if (selectedDecision === clickedDecision) {
			setSelectedDecision(null);
			setActiveDecision(null);
		} else {
			setSelectedDecision(clickedDecision);
			setActiveDecision(clickedDecision);
		}
		setText('');
	};

	const cancelFeedback = () => {
		setActiveDecision(null);
		setSelectedDecision(null);
		setText('');
	};

	const resolveFeedbackDecision = () => activeDecision ?? selectedDecision;

	const buildPayload = (decision) => {
		const payload = {
			decision,
			person: person?._id,
			message: text.trim() || t.feedbackSkippedText,
			device_description: getUserDeviceDescription(),
			source,
		};

		if (source === OPINION_SOURCE.MESSAGE && ratedMessage?.trim()) {
			payload.rated_message = ratedMessage.trim();
		}

		return payload;
	};

	const skipFeedback = () => {
		const decision = resolveFeedbackDecision();

		if (!decision) {
			setActiveDecision(null);
			return;
		}

		handleCreateHandMutation.mutate(buildPayload(decision));

		setActiveDecision(null);
		setText('');
	};

	const submitFeedback = () => {
		const decision = resolveFeedbackDecision();

		if (decision === OPINION_DECISION.HAND_DOWN && text.trim().length < 5) {
			return;
		}

		const payload = buildPayload(decision);
		payload.message = text.trim();

		handleCreateHandMutation.mutate(payload);

		setActiveDecision(null);
		setText('');
	};

	const isSubmitDisabled =
		activeDecision === OPINION_DECISION.HAND_DOWN && text.trim().length < 5;

	return {
		t,
		activeDecision,
		text,
		setText,
		selectedDecision,
		handleIconClick,
		cancelFeedback,
		skipFeedback,
		submitFeedback,
		isSubmitDisabled,
		isSubmitting: handleCreateHandMutation.isLoading,
	};
};

export default useChatFeedback;
